import { BreadcrumbBox } from '@/features/Breadcrumb';
import { ContentContainer } from '@/features/shared';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { MuiContainer } from '@/features/shared/material-ui';
import { colorMap, ColorNames, LoadingStatusValues, SpacingSizes } from '@/utils/constants';
import { resolveTheme } from '@/utils/resolvers';
import React, { memo, useContext, useMemo } from 'react';
import { FilteredListProps } from './FilteredList.interface';
import useStyles from './FilteredList.styles';
import ResultsGrid from './Results/Results';
import TopBarContent from './TopBarContent/TopBarContent';

const FilteredList = ({
  content,
  presentation,
  contentButtons,
  theme,
  labels,
  categoryUrl,
  isSearchPage,
  contentCategoryCardFieldMapper = {},
  contentCategoryKey = '',
  dataLayerSearchAttributesByContentCategory = {},
}: Br.PropsWithContentButtons<FilteredListProps>) => {
  const topBarContent = { ...content };
  const { containerBackgroundColor, includeBreadcrumb, headerBackgroundColor } = presentation;
  const { status } = useContext(ElasticComponentContext);
  const classes = useStyles();

  const topBarContentTheme = useMemo(
    () => resolveTheme('bg-color')({ rawVal: headerBackgroundColor || colorMap.eggshell }),
    [headerBackgroundColor],
  );

  const sameBgColor = useMemo(
    () => headerBackgroundColor === containerBackgroundColor,
    [headerBackgroundColor, containerBackgroundColor],
  );

  const { initial } = LoadingStatusValues;

  return (
    <>
      <ContentContainer
        topSpacing={SpacingSizes.small}
        mobileBottomSpacing={SpacingSizes.none}
        bottomSpacing={SpacingSizes.small}
        topBackgroundColor={headerBackgroundColor || ColorNames.eggshell}
        bottomBackgroundColor={headerBackgroundColor || ColorNames.eggshell}
      >
        <MuiContainer>
          <div>
            {contentButtons?.content}
            {!!includeBreadcrumb && (
              <BreadcrumbBox theme={topBarContentTheme} className={classes.breadcrumb} />
            )}
            <TopBarContent
              theme={topBarContentTheme}
              content={topBarContent}
              presentation={presentation}
              labels={labels}
              isSearchPage={isSearchPage}
              dataLayerSearchAttributesByContentCategory={
                dataLayerSearchAttributesByContentCategory
              }
            />
          </div>
        </MuiContainer>
      </ContentContainer>
      {status !== initial && (
        <ContentContainer
          topSpacing={sameBgColor ? SpacingSizes.none : SpacingSizes.small}
          bottomSpacing={SpacingSizes.small}
          topBackgroundColor={containerBackgroundColor || ColorNames.darkGray}
          bottomBackgroundColor={containerBackgroundColor || ColorNames.darkGray}
        >
          <ResultsGrid
            presentation={presentation}
            theme={theme}
            labels={labels}
            categoryUrl={categoryUrl}
            isCached={content.isCached}
            contentCategoryCardFieldMapper={contentCategoryCardFieldMapper}
            contentCategoryKey={contentCategoryKey}
          />
        </ContentContainer>
      )}
    </>
  );
};

export default memo(FilteredList);
